import { z } from 'zod';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema } from '../money/payment-methods.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';
import {
  PAYMENT_NOTE_MAX_LENGTH,
  PAYMENT_REFERENCE_MAX_LENGTH,
  paymentSchema,
} from './payments.js';

/**
 * A garment that has come BACK.
 *
 * The customer collected a blouse three weeks ago and the sleeve is tight; the lehenga needs
 * taking in before the wedding. Every tailoring shop does this every week, and until now this
 * product had nowhere to put it — which meant a shop either re-opened a delivered order (losing
 * the delivery date it had honestly met) or booked a fresh order (inventing a sale that never
 * happened, and telling the revenue report a lie).
 *
 * Three decisions shape everything below.
 *
 * **It is its own record, not a stage.** Sending a delivered garment back to `stitching` would
 * be the cheap implementation and the wrong one: the order's promised date was met, the karigar
 * was paid, the invoice was issued, and the per-stage timings that price piece work would be
 * rewritten by work that happened a month later. An alteration references the garment it is
 * about and leaves that garment's history alone.
 *
 * **It has its own tiny lifecycle**, three states long, because that is all a shop tracks: it
 * is booked, somebody is doing it, it is ready to collect. The nine-stage production workflow
 * belongs to making a garment, not to letting one out half an inch.
 *
 * **It is usually free**, so `chargePaise` defaults to zero and means it. A re-fit inside a
 * fortnight is the shop's own mistake and the shop eats it; a re-fit because the bride lost
 * weight is chargeable. Both are ordinary, so neither is the default the other has to fight.
 */

/* -------------------------------------------------------------------------------------------
 * Lifecycle
 * ---------------------------------------------------------------------------------------- */

/**
 * Where one alteration has got to.
 *
 * `delivered` is the end of the line and `cancelled` sits outside it, exactly as they do for a
 * garment — but there is deliberately no `qc`, no `cutting` and no `finishing`. A shop that
 * needed those would be making a new garment.
 */
export const ALTERATION_STATUSES = [
  'booked',
  'in_progress',
  'ready',
  'delivered',
  'cancelled',
] as const;
export type AlterationStatus = (typeof ALTERATION_STATUSES)[number];
export const alterationStatusSchema = z.enum(ALTERATION_STATUSES);

/** The linear progression. `cancelled` is reachable from anywhere and is not part of it. */
export const ALTERATION_SEQUENCE = ['booked', 'in_progress', 'ready', 'delivered'] as const;

export const ALTERATION_ORDINAL: Readonly<Record<AlterationStatus, number>> = Object.freeze({
  booked: 0,
  in_progress: 1,
  ready: 2,
  delivered: 3,
  cancelled: 99,
});

/** True while the shop still owes the customer work. What the board's open list filters on. */
export function isOpenAlteration(status: AlterationStatus): boolean {
  return status === 'booked' || status === 'in_progress' || status === 'ready';
}

/**
 * The next state, or `undefined` at the end.
 *
 * Forward only and one at a time, for the same reason a garment may not skip stages: "who was
 * working on this and for how long" is the question the screen exists to answer, and a record
 * that jumped from booked to ready answers it with a guess.
 */
export function nextAlterationStatus(status: AlterationStatus): AlterationStatus | undefined {
  const index = ALTERATION_SEQUENCE.indexOf(status as (typeof ALTERATION_SEQUENCE)[number]);
  if (index < 0) return undefined;
  return ALTERATION_SEQUENCE[index + 1];
}

/* -------------------------------------------------------------------------------------------
 * The record
 * ---------------------------------------------------------------------------------------- */

export const ALTERATION_COMPLAINT_MAX_LENGTH = 500;
export const ALTERATION_NOTES_MAX_LENGTH = 2000;

/** What the customer said was wrong. Required: an alteration with no complaint is a mystery. */
export const alterationComplaintSchema = z
  .string()
  .trim()
  .min(1, 'Say what needs altering')
  .max(ALTERATION_COMPLAINT_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

export const ALTERATION_GARMENT_LABEL_MAX_LENGTH = 80;

/** The fields every alteration carries, whichever way it came in. */
const alterationFieldsSchema = z.object({
  complaint: alterationComplaintSchema,
  /** When the shop promised it back. */
  promisedDate: dateInputSchema.nullish(),
  /**
   * What the shop is charging, in paise. Zero — the default — is a re-fit the shop is doing
   * for nothing, which is most of them.
   */
  chargePaise: nonNegativePaiseSchema.default(0),
  /** The karigar doing it. Absent while nobody has picked it up. */
  assignedToUserId: objectIdSchema.nullish(),
  notes: z.string().trim().max(ALTERATION_NOTES_MAX_LENGTH).nullish(),
});

/**
 * Booking one. Two ways in, and exactly one of them per request:
 *
 * **From a garment this shop stitched** — `orderItemId`. The order and the customer are read FROM
 * it rather than sent alongside it: a client that could name a different customer than the
 * garment's own would be able to file somebody's alteration against somebody else's name.
 *
 * **A walk-in** — `customerId` and the garment as the counter described it. The shop did not make
 * this garment, so there is no order to read anything from. The customer must already be in the
 * book (the counter adds them first); the name and phone are still read from that record, not
 * sent. `garmentTypeKey` is the shop's garment type when the counter picked one, and its label is
 * read from the shop's catalogue; for "Other…" the key is absent and the label is what was typed.
 */
export const createAlterationSchema = alterationFieldsSchema
  .extend({
    orderItemId: objectIdSchema.optional(),
    customerId: objectIdSchema.optional(),
    garmentTypeKey: z.string().trim().min(1).max(60).nullish(),
    garmentTypeLabel: z
      .string()
      .trim()
      .min(1, 'Say what the garment is')
      .max(ALTERATION_GARMENT_LABEL_MAX_LENGTH)
      .optional(),
  })
  .superRefine((input, context) => {
    if (input.orderItemId) {
      if (input.customerId || input.garmentTypeKey || input.garmentTypeLabel) {
        context.addIssue({
          code: 'custom',
          path: ['orderItemId'],
          message: 'A garment from an order carries its own customer and garment type',
        });
      }
      return;
    }
    if (!input.customerId) {
      context.addIssue({ code: 'custom', path: ['customerId'], message: 'Pick the customer' });
    }
    // A picked garment type prints under the shop's own name for it; "Other…" needs a label.
    if (!input.garmentTypeKey && !input.garmentTypeLabel) {
      context.addIssue({
        code: 'custom',
        path: ['garmentTypeLabel'],
        message: 'Say what the garment is',
      });
    }
  });
export type CreateAlterationInput = z.input<typeof createAlterationSchema>;

/** Which garment and whose it is are absent: neither is a thing that changes. */
export const updateAlterationSchema = alterationFieldsSchema
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateAlterationInput = z.input<typeof updateAlterationSchema>;

/**
 * Money taken at the hand-over, in the same request that marks it delivered — so "collected and
 * handed back" is one commit, and a failed payment leaves the garment on the counter.
 */
export const alterationCollectSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema,
  reference: z.string().trim().max(PAYMENT_REFERENCE_MAX_LENGTH).nullish(),
  note: z.string().trim().max(PAYMENT_NOTE_MAX_LENGTH).nullish(),
});
export type AlterationCollectInput = z.input<typeof alterationCollectSchema>;

/** Moving one along. The reason rides with a cancellation, which is the only backward move. */
export const moveAlterationSchema = z.object({
  toStatus: alterationStatusSchema,
  reason: z.string().trim().max(500).nullish(),
  /** Only with `toStatus: 'delivered'`: the balance, taken as it goes out of the door. */
  collect: alterationCollectSchema.nullish(),
});
export type MoveAlterationInput = z.input<typeof moveAlterationSchema>;

export const alterationSchema = z.object({
  id: objectIdSchema,
  /** Its own series, so an alteration is never mistaken for an order at the counter. */
  number: z.string(),
  /** The order and garment it came back from; all three null on a walk-in. */
  orderId: objectIdSchema.nullable(),
  orderNumber: z.string().nullable(),
  orderItemId: objectIdSchema.nullable(),
  /**
   * Frozen at booking, so a later edit on the garment never rewrites this row. The key is null
   * on a walk-in described as "Other…".
   */
  garmentTypeKey: z.string().nullable(),
  garmentTypeLabel: z.string(),
  customerId: objectIdSchema,
  customerName: z.string(),
  customerPhone: z.string(),
  complaint: z.string(),
  status: alterationStatusSchema,
  promisedDate: isoDateStringSchema.nullable(),
  chargePaise: nonNegativePaiseSchema,
  /** The sum of the ledger entries against it. Never typed in. */
  amountPaidPaise: paiseSchema,
  /** What is still to collect, never below zero. */
  outstandingPaise: nonNegativePaiseSchema,
  assignedToUserId: objectIdSchema.nullable(),
  assignedToName: z.string().nullable(),
  notes: z.string().nullable(),
  bookedAt: isoDateStringSchema,
  startedAt: isoDateStringSchema.nullable(),
  readyAt: isoDateStringSchema.nullable(),
  deliveredAt: isoDateStringSchema.nullable(),
  cancelledAt: isoDateStringSchema.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Alteration = z.infer<typeof alterationSchema>;

export const alterationSearchQuerySchema = paginationQuerySchema.extend({
  status: alterationStatusSchema.optional(),
  /** Everything the shop still owes work on — the board's default view. */
  openOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
  customerId: objectIdSchema.optional(),
  assignedToUserId: objectIdSchema.optional(),
  /** Order number, customer name or phone. */
  q: z.string().trim().max(120).optional(),
});
export type AlterationSearchQuery = z.infer<typeof alterationSearchQuerySchema>;

export const alterationPageSchema = z.object({
  items: z.array(alterationSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type AlterationPage = z.infer<typeof alterationPageSchema>;

/* -------------------------------------------------------------------------------------------
 * Money
 * ---------------------------------------------------------------------------------------- */

/** One alteration's ledger: the same shape as an order's, keyed by the alteration. */
export const alterationPaymentsSchema = z.object({
  alterationId: objectIdSchema,
  alterationNumber: z.string(),
  customerName: z.string(),
  customerPhone: z.string(),
  chargePaise: nonNegativePaiseSchema,
  paidPaise: paiseSchema,
  outstandingPaise: nonNegativePaiseSchema,
  creditPaise: nonNegativePaiseSchema,
  suggestedMethod: paymentMethodSchema,
  entries: z.array(paymentSchema),
});
export type AlterationPayments = z.infer<typeof alterationPaymentsSchema>;

export const alterationPaymentResultSchema = z.object({
  recorded: paymentSchema,
  payments: alterationPaymentsSchema,
});
export type AlterationPaymentResult = z.infer<typeof alterationPaymentResultSchema>;
